import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  startProgressBar,
  updateProgressBar,
  closeProgressBar,
  setErrors,
} from 'Actions';
import service from 'Api/service';
import { history } from 'Components/utils/router/history';
import ImportWizardHeader from '../ImportWizardHeader';
import { useImportWizardPreviewQuery } from '../../../types/graphql';
import {
  PRETTY_NAMES,
  followImportProgress,
  ImportProgressPayload,
} from 'HelperFunctions/import_wizard';
import AppState from '../../../types/AppState';
import {
  PreviewRouteParams,
  PreviewRow,
  PreviewUpdate,
  CommitResponse,
} from './Preview.types';

const rowLabel = (row: PreviewRow | PreviewUpdate) =>
  `Row ${row.line ?? '?'}${row.name ? ` · ${row.name}` : ''}`;

// Preview step (D1): what committing the staged batch would do, from the same match pass the commit job
// runs. Commit is only offered when no staged row has problems; the job runs in the background.
const Preview: React.FC = () => {
  const { dataType: importType } = useParams<PreviewRouteParams>();
  const dispatch = useDispatch();
  const business = useSelector((state: AppState) => state.business.business);
  const [committing, setCommitting] = useState(false);
  const { data, loading, error, refetch } = useImportWizardPreviewQuery({
    variables: { importType },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (
      business?.permissions &&
      business.permissions.importWizardEnabled === false
    ) {
      history.push('/unauthorized');
    }
  }, [business]);

  const preview = data?.importWizardPreview;
  const label = PRETTY_NAMES[importType] || 'records';

  const commit = () => {
    if (!preview || preview.errorCount > 0 || committing) return;
    setCommitting(true);
    service
      .post(`${import.meta.env.VITE_API_DOMAIN}/api/imports`, {
        import_type: importType,
      })
      .then((response: { data: { import: number } }) => {
        dispatch(startProgressBar('import'));
        return followImportProgress(response.data.import, (percent) =>
          dispatch(updateProgressBar(percent))
        );
      })
      .then((result: ImportProgressPayload) => {
        dispatch(closeProgressBar());
        history.push(`/importWizard/done/${importType}`, {
          response: result.response as CommitResponse,
        });
      })
      .catch(
        (failure: {
          response?: { data?: { error?: string }; message?: string };
        }) => {
          dispatch(closeProgressBar());
          setCommitting(false);
          dispatch(
            setErrors(
              failure?.response?.data?.error ||
                failure?.response?.message ||
                'The import could not be completed.'
            )
          );
          refetch();
        }
      );
  };

  const renderRows = (
    title: string,
    rows: PreviewRow[],
    id: string,
    tone = ''
  ) =>
    rows.length > 0 && (
      <section className={`importWizardPreviewSection ${tone}`} id={id}>
        <h3>{title}</h3>
        <ul>
          {rows.map((row) => (
            <li key={row.rowId}>
              <b>{rowLabel(row)}</b>
              {row.messages.length > 0 && (
                <span> — {row.messages.join('; ')}</span>
              )}
            </li>
          ))}
        </ul>
      </section>
    );

  return (
    <div className='contentWrapper'>
      <ImportWizardHeader
        importStatus='preview'
        displayButtons={false}
        headerTitle={`Preview ${label} Import`}
      />
      <div className='ImportWizardAdjustContainer importWizardPreview'>
        {loading && <p>Checking your rows…</p>}
        {error && <p className='importWizardMessage warn'>{error.message}</p>}
        {preview && (
          <>
            <div
              className='adjustTableMatches'
              id='import_wizard_preview_counts'
            >
              <span className='matches'>
                <b>{preview.toCreate}</b> to create
              </span>
              <span className='updates'>
                <b>{preview.toUpdate}</b> to update
              </span>
              <span className='skipped'>
                <b>{preview.unchanged}</b> unchanged
              </span>
              <span className='skipped'>
                <b>{preview.toSkip}</b> to skip
              </span>
              <span className={preview.errorCount > 0 ? 'problems' : 'matches'}>
                <b>{preview.errorCount}</b> with problems
              </span>
            </div>
            {preview.ignoredColumns.length > 0 && (
              <p>
                Ignored columns: <b>{preview.ignoredColumns.join(', ')}</b>
              </p>
            )}
            {preview.errorCount > 0 && (
              <p className='importWizardMessage warn'>
                {preview.errorCount}{' '}
                {preview.errorCount === 1 ? 'row has' : 'rows have'} problems;
                fix or delete them in the Adjust step before importing.
              </p>
            )}
            {renderRows(
              'Rows with problems',
              preview.errors,
              'import_wizard_preview_errors',
              'warn'
            )}
            {preview.updates.length > 0 && (
              <section
                className='importWizardPreviewSection'
                id='import_wizard_preview_updates'
              >
                <h3>
                  Updates (only the columns in your file change; a blank cell
                  clears its value)
                </h3>
                {preview.updates.map((row) => (
                  <div key={row.rowId} className='importWizardPreviewUpdate'>
                    <b>{rowLabel(row)}</b>
                    {row.changes && row.changes.length > 0 ? (
                      <table className='grid'>
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Before</th>
                            <th>After</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.changes.map((change) => (
                            <tr key={change.field}>
                              <td>{change.field}</td>
                              <td>{change.before}</td>
                              <td>{change.after}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <span> — no changes</span>
                    )}
                  </div>
                ))}
              </section>
            )}
            {renderRows(
              'Skipped rows',
              preview.skipped,
              'import_wizard_preview_skipped'
            )}
            {renderRows(
              preview.toCreate > preview.creates.length
                ? `New ${label.toLowerCase()} (first ${preview.creates.length} of ${preview.toCreate})`
                : `New ${label.toLowerCase()}`,
              preview.creates,
              'import_wizard_preview_creates'
            )}
          </>
        )}
      </div>
      <footer className='importWizardSubmit'>
        <a
          className='btn cancelBtn'
          id='import_wizard_back'
          onClick={() => history.push(`/importWizard/adjust/${importType}`)}
        >
          Back to Adjust
        </a>
        <a
          id='import_wizard_commit'
          className={`btn submitBtn${!preview || preview.errorCount > 0 || committing ? ' disabled' : ''}`}
          onClick={commit}
        >
          {committing
            ? 'Importing…'
            : `Import ${preview ? preview.toCreate + preview.toUpdate : ''} ${label.toLowerCase()}`}
        </a>
      </footer>
    </div>
  );
};

export default Preview;
